import { lazy, Suspense, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, Landmark, Building2, FileText, Scale, Home as HomeIcon, AlertTriangle, ArrowRight, ShieldCheck, Check, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { savePendingQuestion } from "@/lib/pendingQuestion";
import { trackEvent } from "@/lib/analytics";
import ProductTour from "@/components/marketing/ProductTour";
import { CHATGPT_COMPARISON_SLUG, productConfig } from "@/config/product";

const HeroParticleBackground = lazy(() =>
  import("@hoalaw/ui/hero").then((m) => ({ default: m.default }))
);

const audienceIcons = [Users, Landmark, Building2];

const Home = () => {
  const home = productConfig.canonicalUi.home;
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [showHeroBackground, setShowHeroBackground] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    let timeoutId: number | undefined;
    const scheduleHeroBackground = () => {
      timeoutId = window.setTimeout(() => setShowHeroBackground(true), 1200);
    };

    if (document.readyState === "complete") {
      scheduleHeroBackground();
    } else {
      window.addEventListener("load", scheduleHeroBackground, { once: true });
    }

    return () => {
      window.removeEventListener("load", scheduleHeroBackground);
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  const goToChat = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return navigate("/chatbot");
    // Keep the question out of the URL; fall back to ?q= only when storage is unavailable.
    navigate(savePendingQuestion(trimmed) ? "/chatbot" : `/chatbot?q=${encodeURIComponent(trimmed)}`);
  };

  const handleAsk = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    goToChat(question);
  };

  return (
    <div className="flex flex-col min-h-[80vh]">
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-gray-50 to-gray-100 py-16 md:py-24">
        {showHeroBackground && (
          <Suspense fallback={null}><HeroParticleBackground /></Suspense>
        )}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_14%_18%,hsl(var(--primary)/0.16),transparent_44%),radial-gradient(circle_at_82%_22%,hsl(var(--primary)/0.10),transparent_38%)] opacity-70" aria-hidden="true" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8">
            <div className="lg:col-span-6">
              <p className="inline-flex items-center rounded-full border border-primary/20 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary shadow-sm backdrop-blur">
                {home.eyebrow}
              </p>
              <h1 className="mt-4 text-4xl tracking-tight font-extrabold text-gray-900 sm:mt-5 sm:text-5xl lg:mt-6 xl:text-6xl">
                <span className="block">{home.title}</span>
                <span className="block text-primary">Research with AI</span>
              </h1>
              <p className="mt-3 text-base text-gray-600 sm:mt-5 sm:text-lg">
                {home.subtitle}
              </p>
              <ul className="mt-6 space-y-2">
                {home.trustPoints.map((point) => (
                  <li key={point} className="flex items-start gap-2.5 text-sm text-gray-600 sm:text-base">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <form onSubmit={handleAsk} className="mt-8">
                <label htmlFor="hero-question" className="block text-sm font-semibold text-gray-700">
                  Ask your HOA question
                </label>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <input
                    id="hero-question"
                    name="q"
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    maxLength={500}
                    placeholder="e.g. Can the board fine me without a hearing?"
                    className="h-14 w-full flex-1 rounded-xl border border-gray-300 bg-white px-4 text-base text-gray-900 shadow-sm outline-none transition-colors placeholder:text-gray-400 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30"
                  />
                  <Button
                    type="submit"
                    className="h-14 whitespace-nowrap bg-primary px-7 text-base font-semibold shadow-sm transition-colors hover:bg-primary/90"
                  >
                    Get a cited answer
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
                <p className="mt-2.5 text-sm text-gray-600">{home.note}</p>
              </form>

              <div className="mt-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Or start with a common one
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {home.starterQuestions.map((starter) => (
                    <button
                      key={starter}
                      type="button"
                      onClick={() => goToChat(starter)}
                      className="cursor-pointer rounded-full border border-gray-300 bg-white/80 px-3.5 py-2 text-sm text-gray-700 shadow-sm backdrop-blur transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-[hsl(186_72%_28%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    >
                      {starter}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2">
                <a
                  href="/how-it-works/"
                  className="text-sm font-medium text-gray-600 underline-offset-4 hover:text-primary hover:underline"
                  onClick={() => trackEvent("landing_cta_clicked", {
                    acquisition_page: "home", cta: "how_it_works", cta_target: "/how-it-works/", cta_position: "inline",
                  })}
                >
                  How it works →
                </a>
                <Link to="/pricing" className="text-sm font-medium text-gray-600 underline-offset-4 hover:text-primary hover:underline">
                  See pricing
                </Link>
                <a href={`/questions/${CHATGPT_COMPARISON_SLUG}`} className="text-sm font-medium text-[hsl(186_72%_28%)] hover:underline">
                  Why this beats a general chatbot →
                </a>
              </div>
            </div>
            <div className="mt-12 lg:mt-0 lg:col-span-6">
              <h2 className="mb-3 text-center text-base font-semibold text-gray-800">See what a cited answer looks like</h2>
              <figure className="m-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/[0.03]">
                <figcaption className="flex items-center gap-2 border-b border-gray-200 bg-gray-50/80 px-4 py-2.5">
                  <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
                  <span className="text-xs font-semibold text-gray-700">{home.sampleProductName}</span>
                  <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                    Source-cited
                  </span>
                </figcaption>

                <div className="space-y-4 p-5 sm:p-6">
                  <div className="flex justify-end">
                    <p className="max-w-[85%] rounded-2xl rounded-br-sm border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-800">
                      {home.sampleQuestion}
                    </p>
                  </div>

                  <div className="space-y-3.5">
                    <div>
                      <div className="mb-1.5 flex items-center gap-2">
                        <FileText className="h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
                        <p className="text-[13px] font-semibold uppercase tracking-wide text-gray-900">Short Answer</p>
                      </div>
                      <p className="text-[14px] leading-relaxed text-gray-700">{home.sampleAnswer}</p>
                    </div>

                    <div>
                      <div className="mb-1.5 flex items-center gap-2">
                        <Scale className="h-4 w-4 flex-shrink-0 text-slate-500" aria-hidden="true" />
                        <p className="text-[13px] font-semibold uppercase tracking-wide text-gray-900">{home.sampleLawHeading}</p>
                      </div>
                      <ul className="space-y-1">
                        {home.sampleAnswerSections.statutes.map((line) => (
                          <li key={line} className="flex items-start gap-2 text-[14px] leading-relaxed text-gray-700">
                            <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-300" aria-hidden="true" />
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="rounded-r-md border-l-2 border-primary/20 bg-primary/5 py-2 pl-3 sm:pl-4">
                      <div className="mb-1.5 flex items-center gap-2">
                        <HomeIcon className="h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
                        <p className="text-[13px] font-semibold uppercase tracking-wide text-gray-900">What This Means for Homeowners</p>
                      </div>
                      <ul className="space-y-1">
                        {home.sampleAnswerSections.homeowners.map((line) => (
                          <li key={line} className="flex items-start gap-2 text-[14px] leading-relaxed text-gray-700">
                            <span className="mt-0.5 flex-shrink-0 text-[13px] font-semibold leading-relaxed text-[hsl(186_72%_28%)]" aria-hidden="true">✓</span>
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="rounded-r-md border-l-2 border-amber-200 bg-amber-50/40 py-2 pl-3 sm:pl-4">
                      <div className="mb-1.5 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-500" aria-hidden="true" />
                        <p className="text-[13px] font-semibold uppercase tracking-wide text-gray-900">Common Mistakes to Avoid</p>
                      </div>
                      <p className="flex items-start gap-2 text-[14px] leading-relaxed text-gray-700">
                        <span className="mt-0.5 flex-shrink-0 text-[13px] font-semibold leading-relaxed text-destructive" aria-hidden="true">✕</span>
                        <span>{home.sampleAnswerSections.mistake}</span>
                      </p>
                    </div>

                    <div className="border-t border-gray-100 pt-3">
                      <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
                        <ArrowRight className="h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
                        <span className="text-sm font-semibold text-[hsl(186_72%_28%)]">
                          {home.sampleAnswerSections.nextStep}
                        </span>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 pt-3">
                      <div className="mb-2 flex items-center gap-2">
                        <Link2 className="h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
                        <p className="text-[13px] font-semibold uppercase tracking-widest text-gray-600">References</p>
                      </div>
                      <ul className="flex flex-wrap gap-2">
                        {home.sampleAnswerSections.references.map((ref) => (
                          <li
                            key={ref}
                            className="inline-flex items-center gap-1 rounded-full border border-primary/[0.28] bg-primary/[0.08] px-2.5 py-1 text-xs font-medium text-[hsl(186_72%_28%)]"
                          >
                            {ref}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </figure>
              <p className="mt-3 text-center text-xs text-gray-600">
                Example of a real answer format.
              </p>
            </div>
          </div>
        </div>
      </section>

      <ProductTour />

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">{home.quickStartTitle}</h2>
            <p className="mt-3 text-base text-gray-600">{home.quickStartSubtitle}</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-3">
            {home.quickStartSteps.map(({ title, description }) => (
              <div key={title} className="flex flex-col items-center text-center">
                <div className="mb-4 h-1.5 w-10 rounded-full bg-primary" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                <p className="mt-2 text-sm text-gray-600">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">{home.audienceTitle}</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {home.audiences.map(({ title, description }, index) => {
              const Icon = audienceIcons[index] ?? Users;
              return (
                <div key={title} className="rounded-xl border border-gray-200 p-5">
                  <Icon className="h-6 w-6 text-primary" />
                  <h3 className="mt-3 text-lg font-semibold text-gray-900">{title}</h3>
                  <p className="mt-2 text-sm text-gray-600">{description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-primary py-14">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 md:flex-row md:items-center lg:px-8">
          <div>
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">{home.ctaTitle}</h2>
            <p className="mt-2 text-white/90">{home.ctaDescription}</p>
          </div>
          <Link to="/chat">
            <Button className="bg-white px-7 py-6 text-base text-[hsl(186_72%_28%)] hover:bg-gray-100 whitespace-nowrap">
              {home.ctaButton}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
